<template>
  <div class="capture-root">
    <div class="topbar">
      <img src="@/assets/logogruas.png" alt="HomePage" class="logo-button" @click="goBack" />
      <div class="topbar-actions">
        <label class="objectives-toggle">
          <input type="checkbox" v-model="showObjectives" />
          Mostrar objetivos
        </label>
        <button v-if="canConfigureCaptureDashboard" class="config-btn" @click="openConfig">Configuracion</button>
        <button class="refresh-btn" @click="fetchMonthlyData" :disabled="loading">
          {{ loading ? 'Actualizando...' : 'Actualizar' }}
        </button>
      </div>
    </div>

    <header class="header">
      <div>
        <h1>Captura de servicios</h1>
      </div>
      <label class="month-picker">
        Mes
        <input type="month" v-model="selectedMonth" @change="fetchMonthlyData" />
      </label>
    </header>

    <section class="kpi-strip">
      <article class="kpi-card kpi-card--primary">
        <span class="kpi-label">Servicios capturados</span>
        <strong class="kpi-value">{{ totalServices }}</strong>
      </article>
      <article class="kpi-card kpi-card--accent">
        <span class="kpi-label">Mejor en Dia</span>
        <strong class="kpi-value">{{ bestPerformerByShift.day }}</strong>
      </article>
      <article class="kpi-card kpi-card--accent">
        <span class="kpi-label">Mejor en Tarde</span>
        <strong class="kpi-value">{{ bestPerformerByShift.afternoon }}</strong>
      </article>
      <article class="kpi-card kpi-card--accent">
        <span class="kpi-label">Mejor en Noche</span>
        <strong class="kpi-value">{{ bestPerformerByShift.night }}</strong>
      </article>
      <article class="kpi-card kpi-card--accent">
        <span class="kpi-label">Mejor en Extendido</span>
        <strong class="kpi-value">{{ bestPerformerByShift.extended }}</strong>
      </article>
    </section>

    <section v-if="error" class="error-box">{{ error }}</section>

    <section v-if="visibleRows.length" class="table-wrap">
      <table class="matrix-table">
        <thead>
          <tr>
            <th class="sticky-col">Capturista</th>
            <th
              v-for="day in days"
              :key="`h-${day}`"
              :class="['day-header', { 'week-start-header': isWeekStart(day) }]"
            >
              {{ day }}
            </th>
            <th>Total</th>
            <th v-if="showObjectives">% Meta</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="shift in shiftOrder">
            <tr v-if="rowsByShift[shift] && rowsByShift[shift].length" :key="`shift-header-${shift}`" class="shift-header-row">
              <td :colspan="days.length + (showObjectives ? 3 : 2)" class="shift-header-cell">
                <div class="shift-header-content">
                  <span class="shift-title">{{ shiftLabels[shift] }}</span>
                  <span v-if="showObjectives" class="shift-target">Meta mensual por capturista: {{ targetForShift(shift) }}</span>
                </div>
              </td>
            </tr>
            <tr
              v-for="row in rowsByShift[shift]"
              :key="`${shift}-${row.clerk_id}`"
              :class="rowPerformanceClass(row)"
            >
              <td class="sticky-col sticky-name">
                <div class="name-wrap">
                  <span class="name-text">{{ row.clerk_name }}</span>
                </div>
              </td>
              <td
                v-for="(value, idx) in row.daily"
                :key="`v-${shift}-${row.clerk_id}-${idx}`"
                :class="{ 'week-start-col': isWeekStart(idx + 1) }"
              >
                <button
                  class="day-cell"
                  :class="dayCellClass(Number(value || 0))"
                  :style="dayCellStyle(Number(value || 0), idx + 1, shift)"
                  :disabled="Number(value || 0) <= 0"
                  @click="openDayNotes(row, idx + 1)"
                >
                  {{ Number(value || 0) }}
                </button>
              </td>
              <td class="total-cell">
                <span class="total-pill" :class="totalBadgeClass(Number(row.total || 0))">
                  {{ Number(row.total || 0) }}
                </span>
              </td>
              <td v-if="showObjectives" class="progress-cell">
                <span class="progress-pill" :class="progressClass(row)">
                  {{ formatProgress(row) }}
                </span>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </section>
    <section v-else-if="!loading" class="empty-box">
      No hay capturistas visibles para este mes. Verifica que esten seleccionados y clasificados en un turno.
    </section>

    <transition name="modal-fade">
      <div v-if="showConfigModal" class="modal-overlay" @click.self="closeConfig">
        <div class="modal-card config-modal">
          <div class="modal-head">
            <div>
              <h3>Configuracion de turnos</h3>
              <p class="modal-subtitle">Asigna turnos, visibilidad y metas mensuales por turno.</p>
            </div>
            <button class="small-btn" @click="closeConfig">Cerrar</button>
          </div>

          <div class="modal-actions">
            <button class="small-btn" @click="selectAllDraft">Seleccionar todo</button>
            <button class="small-btn" @click="clearAllDraft">Quitar todo</button>
          </div>

          <div class="targets-grid">
            <label class="target-field">
              <span>Meta Dia</span>
              <input type="number" min="1" step="1" v-model.number="configDraft.shiftTargets.day" />
            </label>
            <label class="target-field">
              <span>Meta Tarde</span>
              <input type="number" min="1" step="1" v-model.number="configDraft.shiftTargets.afternoon" />
            </label>
            <label class="target-field">
              <span>Meta Noche</span>
              <input type="number" min="1" step="1" v-model.number="configDraft.shiftTargets.night" />
            </label>
            <label class="target-field">
              <span>Meta Extendido</span>
              <input type="number" min="1" step="1" v-model.number="configDraft.shiftTargets.extended" />
            </label>
          </div>

          <div class="config-table-wrap">
            <table class="config-table">
              <thead>
                <tr>
                  <th>Capturista</th>
                  <th>Visible</th>
                  <th>Turno</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in rows" :key="`cfg-${row.clerk_id}`">
                  <td>{{ row.clerk_name }}</td>
                  <td>
                    <input
                      type="checkbox"
                      :checked="configDraft.selectedClerks.includes(row.clerk_id)"
                      @change="toggleDraftClerk(row.clerk_id, $event.target.checked)"
                    />
                  </td>
                  <td>
                    <div class="shift-choice">
                      <button
                        class="shift-chip"
                        :class="{ active: configDraft.shiftByClerkId[String(row.clerk_id)] === 'day' }"
                        @click="setDraftShift(row.clerk_id, 'day')"
                      >
                        Dia
                      </button>
                      <button
                        class="shift-chip"
                        :class="{ active: configDraft.shiftByClerkId[String(row.clerk_id)] === 'afternoon' }"
                        @click="setDraftShift(row.clerk_id, 'afternoon')"
                      >
                        Tarde
                      </button>
                      <button
                        class="shift-chip"
                        :class="{ active: configDraft.shiftByClerkId[String(row.clerk_id)] === 'night' }"
                        @click="setDraftShift(row.clerk_id, 'night')"
                      >
                        Noche
                      </button>
                      <button
                        class="shift-chip"
                        :class="{ active: configDraft.shiftByClerkId[String(row.clerk_id)] === 'extended' }"
                        @click="setDraftShift(row.clerk_id, 'extended')"
                      >
                        Extendido
                      </button>
                      <button class="shift-chip shift-chip--clear" @click="setDraftShift(row.clerk_id, '')">
                        Sin clasificar
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="modal-foot">
            <button class="small-btn" @click="closeConfig">Cancelar</button>
            <button class="small-btn primary" :disabled="savingConfig" @click="applyConfig">
              {{ savingConfig ? 'Guardando...' : 'Guardar cambios' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="modal-fade">
      <div v-if="showNotesModal" class="modal-overlay" @click.self="closeNotesModal">
        <div class="modal-card notes-modal">
        <div class="modal-head">
          <div>
            <h3>Notas de {{ notesMeta.clerkName }}</h3>
            <p class="modal-subtitle">{{ notesMeta.date }} · {{ dayNotes.length }} nota(s)</p>
          </div>
          <button class="small-btn" @click="closeNotesModal">Cerrar</button>
        </div>
        <div class="notes-toolbar">
          <span class="notes-chip notes-chip--ok">Completado / Facturado</span>
          <span class="notes-chip notes-chip--active">Activa / Asignada</span>
          <span class="notes-chip notes-chip--other">Otro estatus</span>
        </div>
        <div class="table-wrap">
          <table class="notes-table">
            <thead>
              <tr>
                <th># Nota</th>
                <th>Hora</th>
                <th>Cliente</th>
                <th>Tipo</th>
                <th>Estatus</th>
                <th>Accion</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="note in dayNotes" :key="note.note_ident">
                <td>{{ note.note_number }}</td>
                <td>{{ formatHour(note.request_time) }}</td>
                <td>{{ note.client_name || '—' }}</td>
                <td>{{ note.type_name || '—' }}</td>
                <td>
                  <span class="status-pill" :class="statusBadgeClass(note.status_name)">
                    {{ note.status_name || '—' }}
                  </span>
                </td>
                <td>
                  <button class="small-btn primary" @click="openNote(note.note_ident)">Abrir</button>
                </td>
              </tr>
              <tr v-if="!dayNotes.length">
                <td colspan="6" class="empty-cell">No hay notas para este día.</td>
              </tr>
            </tbody>
          </table>
        </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'CallCenterServiceCaptureDashboard',
  data() {
    const now = new Date()
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
    return {
      permissions: [],
      selectedMonth: month,
      days: [],
      rows: [],
      selectedClerks: [],
      shiftByClerkId: {},
      shiftTargets: {
        day: 100,
        afternoon: 100,
        night: 100,
        extended: 100,
      },
      showObjectives: false,
      loading: false,
      error: '',
      showConfigModal: false,
      showNotesModal: false,
      savingConfig: false,
      configDraft: {
        selectedClerks: [],
        shiftByClerkId: {},
        shiftTargets: {
          day: 100,
          afternoon: 100,
          night: 100,
          extended: 100,
        },
      },
      dayNotes: [],
      notesMeta: {
        clerkName: '',
        date: '',
      },
    }
  },
  computed: {
    canConfigureCaptureDashboard() {
      return this.hasPerm('ui_access_call_center_capture_config')
    },
    shiftOrder() {
      return ['day', 'afternoon', 'night', 'extended']
    },
    shiftLabels() {
      return {
        day: 'Dia',
        afternoon: 'Tarde',
        night: 'Noche',
        extended: 'Extendido',
      }
    },
    visibleRows() {
      const allowed = new Set(this.selectedClerks || [])
      return (this.rows || []).filter((row) => {
        if (!allowed.has(row.clerk_id)) return false
        return this.isValidShift(this.clerkShift(row.clerk_id))
      })
    },
    rowsByShift() {
      const grouped = {
        day: [],
        afternoon: [],
        night: [],
        extended: [],
      }
      for (const row of this.visibleRows) {
        const shift = this.clerkShift(row.clerk_id)
        if (!grouped[shift]) continue
        grouped[shift].push(row)
      }
      this.shiftOrder.forEach((shift) => {
        grouped[shift].sort((a, b) => Number(b.total || 0) - Number(a.total || 0))
      })
      return grouped
    },
    totalServices() {
      return this.visibleRows.reduce((acc, row) => acc + Number(row.total || 0), 0)
    },
    shiftMaxDaily() {
      const maxByShift = { day: 0, afternoon: 0, night: 0, extended: 0 }
      this.shiftOrder.forEach((shift) => {
        const values = (this.rowsByShift[shift] || []).flatMap((row) => (row.daily || []).map((v) => Number(v || 0)))
        maxByShift[shift] = values.length ? Math.max(...values) : 0
      })
      return maxByShift
    },
    maxTotal() {
      if (!this.visibleRows.length) return 0
      return Math.max(...this.visibleRows.map((row) => Number(row.total || 0)))
    },
    bestPerformerByShift() {
      const result = {
        day: 'Sin datos',
        afternoon: 'Sin datos',
        night: 'Sin datos',
        extended: 'Sin datos',
      }
      this.shiftOrder.forEach((shift) => {
        const rows = this.rowsByShift[shift] || []
        if (!rows.length) return
        const best = [...rows].sort((a, b) => Number(b.total || 0) - Number(a.total || 0))[0]
        result[shift] = best?.clerk_name || 'Sin datos'
      })
      return result
    },
  },
  created() {
    this.fetchCurrentUser()
  },
  methods: {
    hasPerm(perm) {
      if (!perm) return false
      if (this.permissions.includes(perm)) return true
      return this.permissions.includes(`access_control.${perm}`)
    },
    async fetchCurrentUser() {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const { data } = await axios.get(`${apiDirectory}/current-user/`, {
          headers: { Authorization: 'Token ' + localStorage.getItem('authToken') },
        })
        this.permissions = data.permissions || []
        if (!this.hasPerm('ui_access_call_center_capture_dashboard')) {
          this.$router.push({ name: 'MainPanel' })
          return
        }
        await this.fetchMonthlyData()
      } catch (error) {
        this.$router.push({ name: 'MainPanel' })
      }
    },
    apiBase() {
      return process.env.API_DIRECTORY || ''
    },
    parseSelectedMonth() {
      const [yearStr, monthStr] = (this.selectedMonth || '').split('-')
      return { year: Number(yearStr), month: Number(monthStr) }
    },
    async fetchMonthlyData() {
      this.loading = true
      this.error = ''
      try {
        const { year, month } = this.parseSelectedMonth()
        const { data } = await axios.get(`${this.apiBase()}/dashboard/rrhh-monthly-clerk-series`, {
          params: { year, month },
          headers: { Authorization: `Token ${localStorage.getItem('authToken')}` },
        })
        this.days = Array.isArray(data?.days) ? data.days : []
        this.rows = Array.isArray(data?.series) ? data.series : []
        await this.fetchConfig()
      } catch (error) {
        this.error = 'No se pudo cargar el dashboard de captura.'
      } finally {
        this.loading = false
      }
    },
    async fetchConfig() {
      try {
        const { data } = await axios.get(`${this.apiBase()}/dashboard/call-center-capture-config`, {
          headers: { Authorization: `Token ${localStorage.getItem('authToken')}` },
        })
        const fromApi = Array.isArray(data?.visible_clerk_ids) ? data.visible_clerk_ids : []
        this.selectedClerks = data?.is_configured ? fromApi : (this.rows || []).map((row) => row.clerk_id)
        this.shiftByClerkId = this.normalizeShiftMap(data?.shift_by_clerk_id || {})
        this.shiftTargets = {
          day: this.normalizeTarget(data?.target_day, 100),
          afternoon: this.normalizeTarget(data?.target_afternoon, 100),
          night: this.normalizeTarget(data?.target_night, 100),
          extended: this.normalizeTarget(data?.target_extended, 100),
        }
      } catch (error) {
        this.selectedClerks = (this.rows || []).map((row) => row.clerk_id)
        this.shiftByClerkId = {}
        this.shiftTargets = { day: 100, afternoon: 100, night: 100, extended: 100 }
      }
      this.syncClerkSelection()
      this.syncShiftMap()
    },
    syncClerkSelection() {
      const validIds = new Set((this.rows || []).map((row) => row.clerk_id))
      this.selectedClerks = (this.selectedClerks || []).filter((id) => validIds.has(id))
    },
    syncShiftMap() {
      const validIds = new Set((this.rows || []).map((row) => row.clerk_id))
      const normalized = {}
      Object.entries(this.shiftByClerkId || {}).forEach(([rawId, rawShift]) => {
        const parsedId = Number(rawId)
        if (!validIds.has(parsedId)) return
        const shift = this.normalizeShift(rawShift)
        if (!this.isValidShift(shift)) return
        normalized[String(parsedId)] = shift
      })
      this.shiftByClerkId = normalized
    },
    normalizeShift(rawShift) {
      const shift = String(rawShift || '').trim().toLowerCase()
      if (shift === 'day') return 'day'
      if (shift === 'afternoon' || shift === 'tarde') return 'afternoon'
      if (shift === 'night') return 'night'
      if (shift === 'extended') return 'extended'
      return ''
    },
    isValidShift(shift) {
      return ['day', 'afternoon', 'night', 'extended'].includes(shift)
    },
    normalizeShiftMap(rawMap) {
      if (!rawMap || typeof rawMap !== 'object') return {}
      const normalized = {}
      Object.entries(rawMap).forEach(([rawId, rawShift]) => {
        const parsedId = Number(rawId)
        if (!Number.isInteger(parsedId)) return
        const shift = this.normalizeShift(rawShift)
        if (!this.isValidShift(shift)) return
        normalized[String(parsedId)] = shift
      })
      return normalized
    },
    normalizeTarget(value, fallback = 100) {
      const parsed = Number(value)
      if (!Number.isFinite(parsed) || parsed < 1) return fallback
      return Math.floor(parsed)
    },
    openConfig() {
      if (!this.canConfigureCaptureDashboard) return
      this.configDraft = {
        selectedClerks: [...(this.selectedClerks || [])],
        shiftByClerkId: { ...(this.shiftByClerkId || {}) },
        shiftTargets: {
          day: this.normalizeTarget(this.shiftTargets.day),
          afternoon: this.normalizeTarget(this.shiftTargets.afternoon),
          night: this.normalizeTarget(this.shiftTargets.night),
          extended: this.normalizeTarget(this.shiftTargets.extended),
        },
      }
      this.showConfigModal = true
    },
    closeConfig() {
      this.showConfigModal = false
    },
    toggleDraftClerk(clerkId, checked) {
      if (checked) {
        if (!this.configDraft.selectedClerks.includes(clerkId)) {
          this.configDraft.selectedClerks = [...this.configDraft.selectedClerks, clerkId]
        }
      } else {
        this.configDraft.selectedClerks = this.configDraft.selectedClerks.filter((id) => id !== clerkId)
      }
    },
    setDraftShift(clerkId, shift) {
      const parsedShift = this.normalizeShift(shift)
      const map = { ...(this.configDraft.shiftByClerkId || {}) }
      if (!this.isValidShift(parsedShift)) {
        delete map[String(clerkId)]
      } else {
        map[String(clerkId)] = parsedShift
      }
      this.configDraft.shiftByClerkId = map
    },
    selectAllDraft() {
      this.configDraft.selectedClerks = (this.rows || []).map((row) => row.clerk_id)
    },
    clearAllDraft() {
      this.configDraft.selectedClerks = []
    },
    async applyConfig() {
      this.savingConfig = true
      try {
        const targetDay = this.normalizeTarget(this.configDraft.shiftTargets.day, 100)
        const targetAfternoon = this.normalizeTarget(this.configDraft.shiftTargets.afternoon, 100)
        const targetNight = this.normalizeTarget(this.configDraft.shiftTargets.night, 100)
        const targetExtended = this.normalizeTarget(this.configDraft.shiftTargets.extended, 100)
        const normalizedMap = this.normalizeShiftMap(this.configDraft.shiftByClerkId || {})
        await axios.put(
          `${this.apiBase()}/dashboard/call-center-capture-config`,
          {
            visible_clerk_ids: this.configDraft.selectedClerks || [],
            shift_by_clerk_id: normalizedMap,
            target_day: targetDay,
            target_afternoon: targetAfternoon,
            target_night: targetNight,
            target_extended: targetExtended,
          },
          {
            headers: { Authorization: `Token ${localStorage.getItem('authToken')}` },
          }
        )
        this.selectedClerks = [...(this.configDraft.selectedClerks || [])]
        this.shiftByClerkId = normalizedMap
        this.shiftTargets = {
          day: targetDay,
          afternoon: targetAfternoon,
          night: targetNight,
          extended: targetExtended,
        }
        this.syncClerkSelection()
        this.syncShiftMap()
        this.closeConfig()
      } catch (error) {
        this.error = 'No se pudo guardar la configuracion de capturistas.'
      } finally {
        this.savingConfig = false
      }
    },
    async openDayNotes(row, day) {
      const { year, month } = this.parseSelectedMonth()
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      this.notesMeta = { clerkName: row.clerk_name, date: dateStr }
      this.dayNotes = []
      this.showNotesModal = true
      try {
        const { data } = await axios.get(`${this.apiBase()}/dashboard/rrhh-clerk-day-notes`, {
          params: { clerk_id: row.clerk_id, date: dateStr },
          headers: { Authorization: `Token ${localStorage.getItem('authToken')}` },
        })
        this.dayNotes = Array.isArray(data?.results) ? data.results : []
      } catch (error) {
        this.dayNotes = []
      }
    },
    closeNotesModal() {
      this.showNotesModal = false
      this.dayNotes = []
    },
    openNote(noteIdent) {
      this.$router.push({ name: 'EditNote', params: { noteID: noteIdent } })
    },
    formatHour(value) {
      if (!value) return '—'
      try {
        return new Date(value).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
      } catch (error) {
        return '—'
      }
    },
    statusBadgeClass(statusName) {
      const status = String(statusName || '').toLowerCase()
      if (status.includes('complet') || status.includes('factur')) return 'status-ok'
      if (status.includes('activ') || status.includes('asign')) return 'status-active'
      return 'status-other'
    },
    dayCellClass(value) {
      if (value <= 0) return ['is-empty']
      return ['is-clickable']
    },
    dayCellStyle(value, day, shift) {
      const max = this.shiftMaxDaily[shift] || 1
      const isWeekStart = this.isWeekStart(day)
      if (!value) {
        return isWeekStart ? { border: '1px solid rgba(37, 99, 235, 0.36)' } : {}
      }
      const intensity = Math.min(0.95, value / max)
      const hue = Math.round(Math.max(0, Math.min(120, intensity * 120)))
      const lightness = Math.round(93 - intensity * 30)
      return {
        background: `hsl(${hue}deg 76% ${lightness}%)`,
        color: intensity > 0.58 ? '#052e16' : '#7f1d1d',
        boxShadow: `inset 0 0 0 1px rgba(15, 23, 42, ${0.12 + intensity * 0.3})`,
        border: isWeekStart ? '1px solid rgba(37, 99, 235, 0.45)' : '1px solid transparent',
      }
    },
    isWeekStart(day) {
      const n = Number(day || 0)
      return n > 0 && ((n - 1) % 7 === 0)
    },
    clerkShift(clerkId) {
      return this.normalizeShift((this.shiftByClerkId || {})[String(clerkId)])
    },
    targetForShift(shift) {
      const value = this.shiftTargets[this.normalizeShift(shift)]
      return this.normalizeTarget(value, 100)
    },
    rowProgress(row) {
      const shift = this.clerkShift(row.clerk_id)
      const target = this.targetForShift(shift)
      const total = Number(row.total || 0)
      if (!target) return 0
      return (total / target) * 100
    },
    progressClass(row) {
      const progress = this.rowProgress(row)
      if (progress >= 100) return 'progress-good'
      if (progress >= 66.6667) return 'progress-mid'
      return 'progress-low'
    },
    formatProgress(row) {
      return `${Math.round(this.rowProgress(row))}%`
    },
    rowPerformanceClass(row) {
      const progress = this.rowProgress(row)
      if (progress >= 100) return 'row-top'
      if (progress >= 66.6667) return 'row-mid'
      return 'row-low'
    },
    totalBadgeClass(total) {
      if (!this.maxTotal) return 'neutral'
      const ratio = total / this.maxTotal
      if (ratio >= 0.85) return 'strong'
      if (ratio >= 0.55) return 'medium'
      return 'soft'
    },
    goBack() {
      this.$router.push({ path: '/call-center' })
    },
  },
}
</script>

<style scoped>
.capture-root {
  min-height: 100vh;
  background:
    radial-gradient(circle at 5% 0%, rgba(14, 165, 233, 0.16), transparent 35%),
    radial-gradient(circle at 92% 2%, rgba(37, 99, 235, 0.14), transparent 32%),
    #eef3fb;
  padding: 76px 16px 24px;
}
.topbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 64px;
  background: #ffc434;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  z-index: 20;
}
.logo-button { height: 65%; object-fit: contain; cursor: pointer; }
.topbar-actions { display: flex; align-items: center; gap: 8px; }
.objectives-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  font-size: 0.8rem;
  color: #111827;
}
.config-btn {
  border: 1px solid #0f172a;
  background: #fff;
  color: #0f172a;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
}
.refresh-btn {
  border: none;
  background: #111827;
  color: #fff;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
}
.header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-bottom: 8px;
  background: rgba(255, 255, 255, 0.76);
  border: 1px solid rgba(148, 163, 184, 0.26);
  border-radius: 14px;
  padding: 8px 12px;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.07);
}
.header h1 { margin: 0; color: #0f172a; font-size: 1.1rem; line-height: 1.1; }
.kpi-strip {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}
.kpi-card {
  border: 1px solid #dbe6f7;
  background: #ffffff;
  border-radius: 12px;
  padding: 10px 12px;
  display: grid;
  gap: 2px;
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.06);
}
.kpi-card--primary {
  border-color: #93c5fd;
  background: linear-gradient(180deg, #ffffff, #eff6ff);
}
.kpi-card--accent {
  border-color: #86efac;
  background: linear-gradient(180deg, #ffffff, #f0fdf4);
}
.kpi-label {
  font-size: 0.74rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 700;
}
.kpi-value {
  font-size: 1.12rem;
  color: #0f172a;
}
.month-picker {
  display: grid;
  gap: 4px;
  font-size: 0.88rem;
  font-weight: 600;
}
.month-picker input {
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px;
}
.table-wrap {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #dbe6f7;
  border-radius: 12px;
  overflow: auto;
  box-shadow: 0 20px 36px rgba(15, 23, 42, 0.09);
}
.matrix-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}
.matrix-table th,
.matrix-table td {
  border-bottom: 1px solid #edf2fb;
  padding: 4px;
  text-align: center;
}
.matrix-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
  color: #0f172a;
  border-bottom: 1px solid #dbe6f7;
  border-right: 1px solid #edf2fb;
  font-weight: 700;
  font-size: 0.8rem;
}
.matrix-table thead th.day-header {
  background: #f8fafc;
  color: #334155;
}
.matrix-table thead th.week-start-header {
  background: #eff6ff;
  color: #1e3a8a;
  box-shadow: inset 0 -2px 0 #bfdbfe;
}
.sticky-col {
  position: sticky;
  left: 0;
  z-index: 3;
  background: #f8fbff;
  text-align: left !important;
}
.sticky-name {
  min-width: 178px;
  font-weight: 700;
}
.name-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}
.name-text {
  color: #0f172a;
}
.top-pill {
  border-radius: 999px;
  font-size: 0.66rem;
  padding: 2px 7px;
  border: 1px solid #16a34a;
  color: #166534;
  background: #dcfce7;
}
.day-cell {
  border: 0;
  background: #f8fafc;
  border-radius: 7px;
  min-width: 24px;
  padding: 5px 4px;
  color: #334155;
  font-size: 0.76rem;
  transition: transform 0.14s ease, box-shadow 0.14s ease, background 0.14s ease;
}
.day-cell.is-clickable {
  cursor: pointer;
  font-weight: 700;
}
.day-cell.is-clickable:hover {
  transform: translateY(-1px);
}
.day-cell.is-empty {
  color: #94a3b8;
  background: #f8fafc;
}
.week-start-col {
  background: rgba(239, 246, 255, 0.65);
}
.shift-header-row td {
  border-bottom: 1px solid #d8e4f5;
  padding: 0 !important;
  background: #ffc434;
}
.shift-header-cell {
  text-align: left !important;
  background: #ffc434;
  color: #111827;
  padding: 0 !important;
}
.shift-header-content {
  width: 100%;
  padding: 7px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.shift-title {
  font-weight: 900;
  font-size: 0.96rem;
  text-align: center;
  flex: 1;
}
.shift-target {
  font-size: 0.78rem;
  color: #111827;
  font-weight: 700;
  margin-left: auto;
  text-align: right;
  white-space: nowrap;
}
.row-top .sticky-col {
  box-shadow: inset 4px 0 0 #16a34a;
}
.row-mid .sticky-col {
  box-shadow: inset 4px 0 0 #0284c7;
}
.row-low .sticky-col {
  box-shadow: inset 4px 0 0 #f59e0b;
}
.total-cell {
  font-weight: 700;
  background: rgba(59, 130, 246, 0.08);
}
.progress-cell {
  background: rgba(248, 250, 252, 0.86);
}
.progress-pill {
  display: inline-flex;
  min-width: 52px;
  justify-content: center;
  border-radius: 999px;
  padding: 3px 8px;
  font-size: 0.74rem;
  font-weight: 800;
  border: 1px solid transparent;
}
.progress-pill.progress-low {
  background: #fee2e2;
  color: #991b1b;
  border-color: #fecaca;
}
.progress-pill.progress-mid {
  background: #fef3c7;
  color: #92400e;
  border-color: #fcd34d;
}
.progress-pill.progress-good {
  background: #dcfce7;
  color: #166534;
  border-color: #86efac;
}
.total-pill {
  display: inline-flex;
  min-width: 36px;
  justify-content: center;
  border-radius: 999px;
  padding: 3px 8px;
  border: 1px solid transparent;
}
.total-pill.strong {
  background: #dcfce7;
  color: #166534;
  border-color: #86efac;
}
.total-pill.medium {
  background: #dbeafe;
  color: #1e3a8a;
  border-color: #93c5fd;
}
.total-pill.soft {
  background: #fef3c7;
  color: #92400e;
  border-color: #fcd34d;
}
.total-pill.neutral {
  background: #e2e8f0;
  color: #334155;
  border-color: #cbd5e1;
}
.empty-box, .error-box {
  margin-top: 12px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 12px;
}
.error-box { color: #b91c1c; border-color: #fecaca; }
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  backdrop-filter: blur(2px);
}
.modal-card {
  width: min(820px, 100%);
  max-height: 82vh;
  background: linear-gradient(180deg, #ffffff, #f8fbff);
  border-radius: 14px;
  border: 1px solid #d7e3f7;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 46px rgba(2, 6, 23, 0.25);
}
.modal-head {
  padding: 12px;
  border-bottom: 1px solid #dbe7f7;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  background: linear-gradient(90deg, #eff6ff, #ecfeff);
}
.modal-head h3 {
  margin: 0;
  color: #0f172a;
}
.modal-subtitle {
  margin: 2px 0 0;
  font-size: 0.82rem;
  color: #475569;
}
.modal-actions {
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fbff;
}
.config-modal {
  width: min(1180px, 100%);
  max-height: 88vh;
}
.targets-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid #e2e8f0;
  background: #f9fbff;
}
.target-field {
  display: grid;
  gap: 5px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #334155;
}
.target-field input {
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 7px 9px;
}
.config-table-wrap {
  overflow: auto;
  padding: 0 12px 12px;
}
.config-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.84rem;
}
.config-table th,
.config-table td {
  border-bottom: 1px solid #ebf1fb;
  padding: 8px;
}
.config-table th {
  text-align: left;
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f1f5f9;
  color: #334155;
}
.shift-choice {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.shift-chip {
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #1e293b;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
}
.shift-chip.active {
  background: #dbeafe;
  border-color: #60a5fa;
  color: #1e3a8a;
}
.shift-chip--clear {
  background: #f8fafc;
  color: #64748b;
}
.modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 12px 12px;
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
}
.small-btn {
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 8px;
  padding: 6px 10px;
  cursor: pointer;
  font-weight: 600;
}
.small-btn.primary {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}
.notes-modal { width: min(960px, 100%); }
.notes-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}
.notes-chip {
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 4px 10px;
  border: 1px solid transparent;
}
.notes-chip--ok {
  background: #dcfce7;
  color: #166534;
  border-color: #86efac;
}
.notes-chip--active {
  background: #dbeafe;
  color: #1e3a8a;
  border-color: #93c5fd;
}
.notes-chip--other {
  background: #f1f5f9;
  color: #334155;
  border-color: #cbd5e1;
}
.notes-table { width: 100%; border-collapse: collapse; font-size: 0.88rem; }
.notes-table th, .notes-table td {
  border-bottom: 1px solid #ebf1fb;
  padding: 8px;
  text-align: left;
}
.notes-table thead th {
  background: #f1f5f9;
  color: #334155;
  font-weight: 700;
}
.notes-table tbody tr:nth-child(even) td {
  background: #fbfdff;
}
.notes-table tbody tr:hover td {
  background: #f0f7ff;
}
.status-pill {
  border-radius: 999px;
  padding: 3px 9px;
  font-size: 0.73rem;
  font-weight: 700;
  border: 1px solid transparent;
}
.status-pill.status-ok {
  color: #166534;
  background: #dcfce7;
  border-color: #86efac;
}
.status-pill.status-active {
  color: #1e3a8a;
  background: #dbeafe;
  border-color: #93c5fd;
}
.status-pill.status-other {
  color: #334155;
  background: #e2e8f0;
  border-color: #cbd5e1;
}
.empty-cell { text-align: center !important; color: #64748b; }
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
.modal-fade-enter-active .modal-card,
.modal-fade-leave-active .modal-card {
  transition: transform 0.22s ease, opacity 0.22s ease;
}
.modal-fade-enter-from .modal-card,
.modal-fade-leave-to .modal-card {
  transform: translateY(10px) scale(0.985);
  opacity: 0.95;
}
@media (prefers-reduced-motion: reduce) {
  .modal-fade-enter-active,
  .modal-fade-leave-active,
  .modal-fade-enter-active .modal-card,
  .modal-fade-leave-active .modal-card {
    transition: none;
  }
}
@media (max-width: 980px) {
  .kpi-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .targets-grid {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 640px) {
  .kpi-strip {
    grid-template-columns: 1fr;
  }
}
</style>
